// app/javascript/components/Chat/Sidebar.js
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Drawer,
  SwipeableDrawer,
  IconButton,
  useMediaQuery,
  Box,
} from "@mui/material";
import SidebarThread from "./SidebarThread";
import SidebarAction from "./SidebarAction";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { useChatContext } from "../../contexts/ChatContext";
import {
  renameThread,
  deleteChatThread,
  getAllUserThreads,
  getUserThreadsPage,
} from "../../api/message";
import { isDarkMode } from "../../utils/constants";
import { useAppContext } from "../../contexts/AppContext";
import WatchlistSidebarPanel from "../WatchlistSidePanel";
import { checkUserHasAccount } from "../../utils/user";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountMenuContent from "./AccountMenuContent";
import UserMemoryPanel from "./UserMemoryPanel";
import TradeProposalsPanel from "./TradeProposalsPanel";
import { checkUserHasSubscription } from "../../utils/user";
import { getUnreadNotificationCount } from "../../api/notifications";
import NotificationPanel from "../Notifications/NotificationPanel";
import AgentTaskControls from "../AgentTaskControls";
import {
  readRememberedActiveChatThread,
  resolveRememberedActiveChatThread,
} from "../../utils/chat_thread_persistence";
import {
  maxUpgradeButtonLabelFor,
  maxUpgradePathFor,
  shouldShowMaxUpgradeCta,
} from "../../utils/max_upgrade_trial";
import TopBar, {
  IconWithBadge,
  MAX_REASONING_MODE,
  displayReasoningMode,
} from "./SidebarTopBar";

const MotionBox = motion(Box);
export const NOTIFICATIONS_DRAWER_WIDTH = 600;
export const CHAT_DRAWER_WIDTH = 300;
export const WATCHLIST_DRAWER_MAX_WIDTH = 420;
export const MEMORY_DRAWER_WIDTH = 700;
export const AGENT_TASKS_DRAWER_WIDTH = 700;
export const getWatchlistDrawerWidth = (viewportWidth) => {
  if (typeof viewportWidth !== "number" || Number.isNaN(viewportWidth)) {
    return WATCHLIST_DRAWER_MAX_WIDTH;
  }
  return viewportWidth > WATCHLIST_DRAWER_MAX_WIDTH
    ? WATCHLIST_DRAWER_MAX_WIDTH
    : viewportWidth;
};
const THREAD_PAGE_SIZE = 20;
const THREAD_LOAD_MORE_THRESHOLD = 180;

export default function Sidebar({
  isSidebarOpen,
  setIsSidebarOpen,
  children,
  selectActiveThread = () => {},
  prefetchThreadMessages = () => {},
  handleAddThread = () => {},
  hasAccount = false,
  requestedThreadUuid = "",
  onQuestionSubmit,
}) {
  const theme = useTheme();

  const { appProps } = useAppContext();
  const navigate = useNavigate();

  const {
    watchlistData,
    setWatchlistData,
    lastRequestedUpdateWatchlist,
    setLastRequestedUpdateWatchlist,
    isRefreshing,
    setIsRefreshing,
    hasErroredWatchlist,
    setHasErroredWatchlist,
    panel,
    setPanel,
    loadingUserData,
    userData,
    setUserData,
  } = appProps;

  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // ≥ 900 px

  const {
    threads,
    setThreads,
    activeThread,
    setActiveThread,
    setLoadingMessageThreads,
    markThreadRead,
    syncUnreadThreadUuids,
    unreadThreadUuids,
  } = useChatContext();

  const isMaxTierSubscriber =
    Boolean(userData?.has_active_subscription) &&
    userData?.subscription_plan_tier === "max";
  const hasSubscription = checkUserHasSubscription(userData);
  const showMaxUpgradeButton = shouldShowMaxUpgradeCta(userData);
  const maxUpgradeHref = maxUpgradePathFor(userData);
  const maxUpgradeLabel = maxUpgradeButtonLabelFor(userData);
  const eligibleForProTrial = userData?.eligible_for_pro_trial !== false;
  const canSwitchThreads = isMaxTierSubscriber;
  const preAccountUser = !checkUserHasAccount(appProps.userData);

  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === "undefined") return WATCHLIST_DRAWER_MAX_WIDTH;
    return window.innerWidth;
  });

  const [reasoningMode, setReasoningMode] = useState(
    userData?.user_account_info?.reasoning_mode || "standard",
  );

  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const unreadThreadCount = useMemo(
    () =>
      Object.values(unreadThreadUuids || {}).reduce(
        (count, hasUnread) => (hasUnread ? count + 1 : count),
        0,
      ),
    [unreadThreadUuids],
  );
  const closedSidebarBadgeCount = unreadNotificationCount + unreadThreadCount;

  const refreshUnreadCount = React.useCallback(async () => {
    try {
      const n = await getUnreadNotificationCount();
      setUnreadNotificationCount(Number(n) || 0);
    } catch {}
  }, []);

  useEffect(() => {
    if (!preAccountUser) {
      refreshUnreadCount();
      const intervalId = setInterval(refreshUnreadCount, 30_000);
      return () => clearInterval(intervalId);
    }
  }, [preAccountUser, refreshUnreadCount]);

  useEffect(() => {
    const handler = () => refreshUnreadCount();
    window.addEventListener("user-notifications:updated", handler);
    return () =>
      window.removeEventListener("user-notifications:updated", handler);
  }, [refreshUnreadCount]);

  useEffect(() => {
    setReasoningMode(userData?.user_account_info?.reasoning_mode || "standard");
  }, [userData?.user_account_info?.reasoning_mode]);

  const handleReasoningModeChange = async (event) => {
    const mode = event.target.value;
    const previousMode = userData?.user_account_info?.reasoning_mode || "standard";
    if (mode === MAX_REASONING_MODE && !isMaxTierSubscriber) {
      setReasoningMode(displayReasoningMode(previousMode, isMaxTierSubscriber));
      navigate(maxUpgradeHref);
      return;
    }

    setReasoningMode(mode);
    try {
      const res = await fetch("/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation ($reasoningMode: String!) {
              updateUserReasoningMode(reasoningMode: $reasoningMode) {
                status
                reasoningMode
              }
            }
          `,
          variables: { reasoningMode: mode },
        }),
      });
      const json = await res.json();
      const payload = json?.data?.updateUserReasoningMode;
      if (!payload || payload.status !== "200") {
        throw new Error(payload?.error || "Save failed");
      }
      // persist into local userData so the rest of the app can see it
      setUserData((prev) => ({
        ...prev,
        user_account_info: {
          ...(prev.user_account_info || {}),
          reasoning_mode: payload.reasoningMode || mode,
        },
      }));
    } catch (e) {
      console.error("update reasoning mode failed", e);
      // revert on error
      setReasoningMode(displayReasoningMode(previousMode, isMaxTierSubscriber));
    }
  };

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuThread, setMenuThread] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [renamingThreadUuid, setRenamingThreadUuid] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const openAccountMenu = (e) => setAccountAnchorEl(e.currentTarget);
  const closeAccountMenu = () => setAccountAnchorEl(null);

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [sidebarScrollToThreadUuid, setSidebarScrollToThreadUuid] =
    useState(null);
  const loadingMoreRef = useRef(false);
  const initialThreadSelectionAttemptedRef = useRef(false);

  const dedupeByUuid = (arr) => {
    const seen = new Set();
    return arr.filter((t) =>
      seen.has(t.threadUuid) ? false : (seen.add(t.threadUuid), true),
    );
  };

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || loading || !hasMore) return;
    loadingMoreRef.current = true;
    setLoading(true);
    try {
      const page = await getUserThreadsPage({
        limit: THREAD_PAGE_SIZE,
        offset,
      });
      if ((page && page.length == 0) || page.error) {
        setHasMore(false);
      } else if (page && !page.error) {
        setThreads((prev) => {
          const safePrev = Array.isArray(prev) ? prev : [];
          const safePage = Array.isArray(page) ? page : [];
          const next = dedupeByUuid([...safePrev, ...safePage]);
          syncUnreadThreadUuids(next, { merge: true });
          return next;
        });
        setOffset((o) => o + page.length);
        setHasMore(page.length === THREAD_PAGE_SIZE);
      } else if (page?.error) {
        setError(page.error);
      }
    } finally {
      setLoading(false);
      loadingMoreRef.current = false;
    }
  }, [loading, hasMore, offset, setThreads, syncUnreadThreadUuids]);

  const renameInputRef = useRef(null);
  const sentinelRef = useRef(null);
  const scrollRef = useRef(null); // <-- scroller for SidebarThread only

  const handleThreadListScroll = () => {
    if (panel !== "chat" || loading || !hasMore) return;
    const rootEl = scrollRef.current;
    if (!rootEl) return;
    const distanceFromBottom =
      rootEl.scrollHeight - rootEl.scrollTop - rootEl.clientHeight;
    if (distanceFromBottom <= THREAD_LOAD_MORE_THRESHOLD) {
      loadMore();
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const watchlistDrawerWidth = getWatchlistDrawerWidth(viewportWidth);
  let drawerWidth = CHAT_DRAWER_WIDTH;
  if (panel === "watchlist") drawerWidth = watchlistDrawerWidth;
  if (panel === "trade_proposals") drawerWidth = watchlistDrawerWidth;
  if (panel === "memory") drawerWidth = MEMORY_DRAWER_WIDTH;
  if (panel === "agent_tasks") drawerWidth = AGENT_TASKS_DRAWER_WIDTH;
  if (panel === "notifications") drawerWidth = NOTIFICATIONS_DRAWER_WIDTH;

  useEffect(() => {
    if (renamingThreadUuid && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingThreadUuid]);

  useEffect(() => {
    if (loadingUserData || Object.keys(userData).length == 0) return;
    if (!checkUserHasAccount(userData)) {
      setLoading(false);
      setLoadingMessageThreads(false); // if pre account user, set laoding false.
      syncUnreadThreadUuids([]);
      return;
    }
    let isMounted = true;
    const fetchThreads = async () => {
      setLoading(true);
      try {
        const data = await getUserThreadsPage({
          limit: THREAD_PAGE_SIZE,
          offset: 0,
          sort: "updated_desc",
        });
        if (!isMounted) return;
        if (data && data.error) {
          setError(data.error);
        } else {
          const safeData = Array.isArray(data) ? data : [];
          setThreads((prev) => {
            const existingTempThreads = Array.isArray(prev)
              ? prev.filter((thread) => thread?.threadUuid?.startsWith("temp-"))
              : [];
            if (!existingTempThreads.length) return safeData;
            return dedupeByUuid([...existingTempThreads, ...safeData]);
          });
          syncUnreadThreadUuids(safeData);
          setOffset(safeData.length);
          setHasMore(safeData.length === THREAD_PAGE_SIZE);
        }
      } catch (e) {
        if (isMounted) setError(e.message || "Something went wrong");
      } finally {
        if (isMounted) setLoading(false);
        setLoadingMessageThreads(false);
      }
    };
    fetchThreads();
    return () => {
      isMounted = false;
    };
  }, [
    loadingUserData,
    userData?.user_id,
    userData?.uuidHasAccount,
    syncUnreadThreadUuids,
  ]);

  useEffect(() => {
    initialThreadSelectionAttemptedRef.current = false;
  }, [requestedThreadUuid, userData?.user_id, userData?.uuid]);

  useEffect(() => {
    if (activeThread) {
      initialThreadSelectionAttemptedRef.current = false;
    }
  }, [activeThread]);

  useEffect(() => {
    if (activeThread) return;
    if (loadingUserData || loading) return;
    if (initialThreadSelectionAttemptedRef.current) return;

    initialThreadSelectionAttemptedRef.current = true;
    const safeThreads = Array.isArray(threads) ? threads : [];
    const normalizedRequestedThreadUuid = String(
      requestedThreadUuid || "",
    ).trim();
    if (
      normalizedRequestedThreadUuid &&
      !normalizedRequestedThreadUuid.startsWith("temp-")
    ) {
      const requestedThread =
        safeThreads.find(
          (thread) => thread?.threadUuid === normalizedRequestedThreadUuid,
        ) || {
          threadUuid: normalizedRequestedThreadUuid,
          title: "Chat",
          isAdminTargeted: false,
        };
      selectActiveThread(requestedThread);
      return;
    }

    const rememberedScope = {
      user_id: userData?.user_id,
      uuid: userData?.uuid,
      current_session_id: userData?.current_session_id,
    };
    const rememberedThread = readRememberedActiveChatThread(rememberedScope);
    const restorableThread = resolveRememberedActiveChatThread(
      rememberedThread,
      safeThreads,
    );

    if (restorableThread) {
      selectActiveThread(restorableThread);
      return;
    }

    handleAddThread();
  }, [
    activeThread,
    handleAddThread,
    loading,
    loadingUserData,
    requestedThreadUuid,
    selectActiveThread,
    threads,
    userData?.current_session_id,
    userData?.user_id,
    userData?.uuid,
  ]);

  useEffect(() => {
    if (panel !== "chat") return;
    if (!sidebarScrollToThreadUuid) return;
    const hasThreadLoaded = (threads || []).some(
      (t) => t.threadUuid === sidebarScrollToThreadUuid,
    );
    if (hasThreadLoaded) return;
    if (!hasMore) {
      setSidebarScrollToThreadUuid(null);
      return;
    }
    if (!loading) {
      loadMore();
    }
  }, [panel, sidebarScrollToThreadUuid, threads, hasMore, loading, loadMore]);

  // Observe within the SidebarThread scroller only
  useEffect(() => {
    if (loading) return;
    if (panel !== "chat") return;
    if (!hasMore) return;
    const el = sentinelRef.current;
    const rootEl = scrollRef.current;
    if (!el || !rootEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loading) {
          loadMore();
        }
      },
      { root: rootEl, rootMargin: "200px 0px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [panel, hasMore, loading, loadMore]);

  useEffect(() => {
    if (panel !== "chat" || loading || !hasMore) return;
    const rootEl = scrollRef.current;
    if (!rootEl) return;
    // Fallback for mobile drawer + observer edge cases: if list doesn't overflow yet,
    // proactively fetch more pages until it does (or no more pages exist).
    if (rootEl.scrollHeight <= rootEl.clientHeight + 1) {
      loadMore();
    }
  }, [panel, threads, hasMore, loading, loadMore]);

  useEffect(() => {
    if (panel !== "chat") return;
    const nextThreads = (threads || [])
      .filter(
        (thread) =>
          thread?.threadUuid &&
          !thread.threadUuid.startsWith("temp-") &&
          thread.threadUuid !== activeThread?.threadUuid,
      )
      .slice(0, 4);

    nextThreads.forEach((thread) => {
      prefetchThreadMessages(thread);
    });
  }, [panel, threads, activeThread?.threadUuid, prefetchThreadMessages]);

  const handleOpenMenu = (event, t) => {
    event.stopPropagation();
    setMenuThread(t);
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuThread(null);
  };
  const startRenaming = () => {
    setRenamingThreadUuid(menuThread.threadUuid);
    setNewTitle(menuThread.title || "");
    handleCloseMenu();
  };
  const cancelRenaming = () => {
    setRenamingThreadUuid(null);
    setNewTitle("");
  };
  const confirmRename = async () => {
    try {
      const threadTitle = await renameThread(newTitle, renamingThreadUuid);
      if (!threadTitle) throw new Error("Rename failed on the server");
      setThreads((prev) =>
        prev.map((t) =>
          t.threadUuid === renamingThreadUuid
            ? { ...t, title: threadTitle }
            : t,
        ),
      );
      if (activeThread.threadUuid === renamingThreadUuid) {
        setActiveThread((prev) => ({ ...prev, title: newTitle }));
      }
      setRenamingThreadUuid(null);
      setNewTitle("");
    } catch (error) {
      console.log(error);
    }
  };
  const handleDelete = () => {
    try {
      deleteChatThread(menuThread.threadUuid);
      markThreadRead(menuThread.threadUuid);
      setThreads((prev) => {
        const next = prev.filter((t) => t.threadUuid !== menuThread.threadUuid);
        if (activeThread?.threadUuid === menuThread.threadUuid && next.length) {
          selectActiveThread(next[0]);
        }
        return next;
      });
    } catch (error) {
      console.warn("[deleteChatThread]", error);
    } finally {
      handleCloseMenu();
    }
  };

  return (
    <>
      {isDesktop ? (
        <Drawer
          variant="persistent"
          open={isSidebarOpen}
          sx={{
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
            width: drawerWidth,
            maxWidth: "70%",
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              maxWidth: "70%",
              backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
              display: "flex",
              borderRight: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
              boxShadow: isDarkMode
                ? "rgba(0,0,0,0.4) 0px 10px 30px"
                : "rgba(0,0,0,0.08) 0px 10px 30px",
            },
          }}
        >
          <TopBar
            panel={panel}
            setPanel={setPanel}
            isDesktop={isDesktop}
            hasSubscription={hasSubscription}
            preAccountUser={preAccountUser}
            eligibleForProTrial={eligibleForProTrial}
            isMaxTierSubscriber={isMaxTierSubscriber}
            navigate={navigate}
            setIsSidebarOpen={setIsSidebarOpen}
            reasoningMode={reasoningMode}
            onChangeReasoningMode={handleReasoningModeChange}
            unreadNotificationCount={unreadNotificationCount}
            showMaxUpgradeButton={showMaxUpgradeButton}
            maxUpgradeHref={maxUpgradeHref}
            maxUpgradeLabel={maxUpgradeLabel}
          />

          {/* Column: non-scroll header/controls + scrollable thread list */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {panel === "chat" ? (
              <>
                <Box sx={{ pt: 1 }}>
                  <AnimatePresence mode="wait">
                    <MotionBox
                      key="sidebar-action"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 0.25 }}
                    >
                      <Box sx={{ height: "20px" }} />
                      <SidebarAction
                        onQuestionSubmit={onQuestionSubmit}
                        handleAddThread={handleAddThread}
                        preAccountUser={preAccountUser}
                        hasSubscription={hasSubscription}
                        canSwitchThreads={canSwitchThreads}
                        openWatchlist={() => setPanel("watchlist")}
                        openTradeProposals={() => setPanel("trade_proposals")}
                        openMemory={() => setPanel("memory")}
                        openAgentTasks={() => setPanel("agent_tasks")}
                        selectActiveThread={selectActiveThread}
                        setSidebarScrollToThreadUuid={
                          setSidebarScrollToThreadUuid
                        }
                      />
                    </MotionBox>
                  </AnimatePresence>
                </Box>

                {/* Scroll only this area */}
                <Box
                  ref={scrollRef}
                  onScroll={handleThreadListScroll}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    px: 0,
                    pb: 1,
                    backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
                  }}
                >
                  <SidebarThread
                    preAccountUser={preAccountUser}
                    threads={threads}
                    hasSubscription={hasSubscription}
                    canSwitchThreads={canSwitchThreads}
                    renamingThreadUuid={renamingThreadUuid}
                    renameInputRef={renameInputRef}
                    newTitle={newTitle}
                    setNewTitle={setNewTitle}
                    confirmRename={confirmRename}
                    cancelRenaming={cancelRenaming}
                    selectActiveThread={selectActiveThread}
                    prefetchThreadMessages={prefetchThreadMessages}
                    handleOpenMenu={handleOpenMenu}
                    anchorEl={anchorEl}
                    handleCloseMenu={handleCloseMenu}
                    startRenaming={startRenaming}
                    handleDelete={handleDelete}
                    setIsSidebarOpen={setIsSidebarOpen}
                    scrollToThreadUuid={sidebarScrollToThreadUuid}
                    onThreadScrollHandled={() =>
                      setSidebarScrollToThreadUuid(null)
                    }
                  />
                  <div ref={sentinelRef} style={{ height: 1 }} />
                </Box>
              </>
            ) : (
              // Watchlist panel (non-scrolling wrapper; inner component can manage its own)
              <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <AnimatePresence mode="wait">
                  <MotionBox
                    key={panel}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.25 }}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ mt: 1, flex: 1, minHeight: 0 }}>
                      {panel === "watchlist" ? (
                        <WatchlistSidebarPanel
                          watchlistData={watchlistData}
                          setWatchlistData={setWatchlistData}
                          onQuestionSubmit={onQuestionSubmit}
                          lastRequestedUpdateWatchlist={
                            lastRequestedUpdateWatchlist
                          }
                          setLastRequestedUpdateWatchlist={
                            setLastRequestedUpdateWatchlist
                          }
                          isRefreshing={isRefreshing}
                          setIsRefreshing={setIsRefreshing}
                          hasErroredWatchlist={hasErroredWatchlist}
                          setHasErroredWatchlist={setHasErroredWatchlist}
                          userData={appProps.userData}
                          hasSubscription={hasSubscription}
                          setActivePanel={() => setPanel("chat")}
                        />
                      ) : panel === "trade_proposals" ? (
                        <TradeProposalsPanel
                          userData={appProps.userData}
                          onQuestionSubmit={onQuestionSubmit}
                          setActivePanel={() => setPanel("chat")}
                        />
                      ) : panel === "memory" ? (
                        <UserMemoryPanel
                          userData={appProps.userData}
                          setActivePanel={() => setPanel("chat")}
                        />
                      ) : panel === "agent_tasks" ? (
                        <AgentTaskControls panelMode />
                      ) : panel === "notifications" ? (
                        <NotificationPanel
                          onUnreadCountChange={refreshUnreadCount}
                        />
                      ) : null}
                    </Box>
                  </MotionBox>
                </AnimatePresence>
              </Box>
            )}
          </Box>

          <SidebarAccountFooter
            user={appProps.userData}
            onOpenMenu={openAccountMenu}
          />
        </Drawer>
      ) : (
        <SwipeableDrawer
          anchor="left"
          open={isSidebarOpen}
          onOpen={() => setIsSidebarOpen(true)}
          onClose={() => setIsSidebarOpen(false)}
          swipeAreaWidth={24}
          sx={{
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
            width: drawerWidth,
            maxWidth: "90%",
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              maxWidth: "90%",
              backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <TopBar
            panel={panel}
            setPanel={setPanel}
            isDesktop={isDesktop}
            hasSubscription={hasSubscription}
            preAccountUser={preAccountUser}
            eligibleForProTrial={eligibleForProTrial}
            isMaxTierSubscriber={isMaxTierSubscriber}
            navigate={navigate}
            setIsSidebarOpen={setIsSidebarOpen}
            reasoningMode={reasoningMode}
            onChangeReasoningMode={handleReasoningModeChange}
            unreadNotificationCount={unreadNotificationCount}
            showMaxUpgradeButton={showMaxUpgradeButton}
            maxUpgradeHref={maxUpgradeHref}
            maxUpgradeLabel={maxUpgradeLabel}
          />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {panel === "chat" ? (
              <>
                <Box sx={{ pt: 1 }}>
                  <AnimatePresence mode="wait">
                    <MotionBox
                      key="sidebar-action-m"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 0.25 }}
                    >
                      <Box sx={{ height: "20px" }} />
                      <SidebarAction
                        onQuestionSubmit={onQuestionSubmit}
                        handleAddThread={handleAddThread}
                        preAccountUser={preAccountUser}
                        hasSubscription={hasSubscription}
                        canSwitchThreads={canSwitchThreads}
                        openWatchlist={() => setPanel("watchlist")}
                        openTradeProposals={() => setPanel("trade_proposals")}
                        openMemory={() => setPanel("memory")}
                        openAgentTasks={() => setPanel("agent_tasks")}
                        selectActiveThread={selectActiveThread}
                        setSidebarScrollToThreadUuid={
                          setSidebarScrollToThreadUuid
                        }
                      />
                    </MotionBox>
                  </AnimatePresence>
                </Box>

                <Box
                  ref={scrollRef}
                  onScroll={handleThreadListScroll}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    px: 0,
                    pb: 1,
                    backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
                  }}
                >
                  <SidebarThread
                    preAccountUser={preAccountUser}
                    threads={threads}
                    hasSubscription={hasSubscription}
                    canSwitchThreads={canSwitchThreads}
                    renamingThreadUuid={renamingThreadUuid}
                    renameInputRef={renameInputRef}
                    newTitle={newTitle}
                    setNewTitle={setNewTitle}
                    confirmRename={confirmRename}
                    cancelRenaming={cancelRenaming}
                    selectActiveThread={selectActiveThread}
                    prefetchThreadMessages={prefetchThreadMessages}
                    handleOpenMenu={handleOpenMenu}
                    anchorEl={anchorEl}
                    handleCloseMenu={handleCloseMenu}
                    startRenaming={startRenaming}
                    handleDelete={handleDelete}
                    setIsSidebarOpen={setIsSidebarOpen}
                    scrollToThreadUuid={sidebarScrollToThreadUuid}
                    onThreadScrollHandled={() =>
                      setSidebarScrollToThreadUuid(null)
                    }
                  />
                  <div ref={sentinelRef} style={{ height: 1 }} />
                </Box>
              </>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <AnimatePresence mode="wait">
                  <MotionBox
                    key={panel}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.25 }}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ mt: 1, flex: 1, minHeight: 0 }}>
                      {panel === "watchlist" ? (
                        <WatchlistSidebarPanel
                          watchlistData={watchlistData}
                          setWatchlistData={setWatchlistData}
                          onQuestionSubmit={onQuestionSubmit}
                          lastRequestedUpdateWatchlist={
                            lastRequestedUpdateWatchlist
                          }
                          setLastRequestedUpdateWatchlist={
                            setLastRequestedUpdateWatchlist
                          }
                          isRefreshing={isRefreshing}
                          setIsRefreshing={setIsRefreshing}
                          hasErroredWatchlist={hasErroredWatchlist}
                          setHasErroredWatchlist={setHasErroredWatchlist}
                          userData={appProps.userData}
                          hasSubscription={hasSubscription}
                          setActivePanel={() => setPanel("chat")}
                        />
                      ) : panel === "trade_proposals" ? (
                        <TradeProposalsPanel
                          userData={appProps.userData}
                          onQuestionSubmit={onQuestionSubmit}
                          setActivePanel={() => setPanel("chat")}
                        />
                      ) : panel === "memory" ? (
                        <UserMemoryPanel
                          userData={appProps.userData}
                          setActivePanel={() => setPanel("chat")}
                        />
                      ) : panel === "agent_tasks" ? (
                        <AgentTaskControls panelMode />
                      ) : panel === "notifications" ? (
                        <NotificationPanel
                          onUnreadCountChange={refreshUnreadCount}
                        />
                      ) : null}
                    </Box>
                  </MotionBox>
                </AnimatePresence>
              </Box>
            )}
          </Box>

          <SidebarAccountFooter
            user={appProps.userData}
            onOpenMenu={openAccountMenu}
          />
        </SwipeableDrawer>
      )}

      <AccountMenuContent
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={closeAccountMenu}
        appProps={appProps}
      />

      {/* Launcher when sidebar is closed */}
      {!isSidebarOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 8,
            left: 8,
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <IconWithBadge count={closedSidebarBadgeCount}>
            <IconButton
              onClick={() => setIsSidebarOpen(true)}
              sx={{ backgroundColor: isDarkMode ? "#222" : "#D9E2F8" }}
              aria-label="open sidebar"
            >
              <MenuIcon sx={{ color: isDarkMode ? "white" : "black" }} />
            </IconButton>
          </IconWithBadge>
        </Box>
      )}

      <main style={{ transition: "margin-left 250ms" }}>{children}</main>
    </>
  );
}

const SidebarAccountFooter = ({ user, onOpenMenu }) => {
  const initials = user?.user_initial ?? "•";
  const name = user?.user_account_info?.full_name;
  if (!name) return null;

  return (
    <Box
      className={"navbar-text-styles"}
      onClick={onOpenMenu}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 2,
        py: 1.5,
        borderTop: "1px solid",
        borderColor: isDarkMode ? "#2f2f2f" : "#e7e7e7",
        cursor: "pointer",
        position: "sticky",
        bottom: 0,
        bgcolor: isDarkMode ? "#222222" : "#F9F9F9",
        zIndex: 1,
      }}
      aria-label="Account settings menu"
      role="button"
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          display: "grid",
          placeItems: "center",
          borderRadius: "50%",
          border: isDarkMode ? "1.5px solid #F5F5F5" : "1.5px solid #121212",
          background: isDarkMode ? "#121212" : "#F5F9FE",
          color: isDarkMode ? "#F5F5F5" : "#121212",
          fontSize: 13,
          fontWeight: 500,
          flex: "0 0 auto",
        }}
      >
        {initials}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          className="base-text"
          sx={{
            fontSize: 14,
            color: isDarkMode ? "#fff" : "#111",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={name}
        >
          {name}
        </Box>
      </Box>

      <ChevronRightIcon
        sx={{ opacity: 0.7, color: isDarkMode ? "#fff" : "#111" }}
      />
    </Box>
  );
};
